// Decides whether an OCR reading of a page is trustworthy, so a sideways or unreadable scan is reported
// as such instead of as "Recognised N words". Pure and unit-tested.

export interface OcrQualityInput {
  text: string;
  /** Tesseract's own 0-100 confidence for the page, when known. */
  confidence?: number;
}

const WORD = new RegExp("[\\p{L}\\p{N}]", "u");
const NON_LETTER = new RegExp("[^\\p{L}]", "gu");

/** Share of word-like tokens that do not look like words: stray brackets or pipes inside, or long runs without a vowel. */
export function garbageFraction(text: string): number {
  const tokens = text.split(/\s+/).filter((t) => WORD.test(t));
  if (tokens.length === 0) return 0;
  let bad = 0;
  for (const t of tokens) {
    const letters = t.replace(NON_LETTER, "");
    const stray = /[{}|\\^~<>_[\]]/.test(t.replace(/^[[({"'“]+|[\])}"'”.,;:!?]+$/g, ""));
    const noVowel = letters.length >= 4 && !/[aeiouyàâäéèêëîïôöùûüáíóúñ]/i.test(letters) && /^[a-z]+$/i.test(letters);
    if (stray || noVowel) bad++;
  }
  return bad / tokens.length;
}

/** True when the page reads like noise: low engine confidence, or most "words" are not words. Needs a few words to judge. */
export function looksUnreadable(input: OcrQualityInput): boolean {
  const words = input.text.split(/\s+/).filter((t) => WORD.test(t));
  if (typeof input.confidence === "number" && input.confidence < 50) return true;
  if (words.length < 3) return false;
  return garbageFraction(input.text) > 0.4 || (typeof input.confidence === "number" && input.confidence < 62 && garbageFraction(input.text) > 0.2);
}
